import { DateTimeUnit } from 'luxon';
import { GTMEvent } from './google-tag-manager';

declare global {
    interface Window {
        dataLayer: GTMEvent[]; // For Google Tag Manager
        __mkEnv: PublicEnv; // For env variables
        __isTestEnvironment: boolean; // To determine if we are in a test environment
    }
}

type AWSEnvironment = 'dev' | 'pilot' | 'prod';
export type Environment = AWSEnvironment | 'bitbucket' | 'docker' | 'local' | undefined;

export type TimeUnit = 'month' | 'quarter' | 'year';
export enum TimeKeyFormat {
    month = 'yyyy-MM',
    quarter = 'yyyy-Qq',
    year = 'yyyy',
}

export interface MKProcessEnv {
    SECRETS_MANAGER_ENV: string;
}

export interface MKSecretsManagerEnv {
    ENVIRONMENT: Environment;
    MK_API_SERVER_EXTERNAL_URL: string;
    INSIGHT_URL: string;
    INSIGHT_URL_CHINA: string;
    SALESFORCE_COMMUNITY_URL: string;
    MYPARTS_URL: string;
    MYPARTS_INTERNAL_URL: string;
    GTM_ID: string;
    TECHSIGHT_URL: string;
}

export type MKProcessEnvKey = Extract<keyof MKProcessEnv, string>;
export type MKProcessEnvSecretsManagerEnvKey = Extract<keyof MKSecretsManagerEnv, string>;

export interface PublicEnv {
    apiBaseURL: string;
    insightURL: string;
    insightURLChina: string;
    salesforceCommunityURL: string;
    myPartsURL: string;
    myPartsInternalURL: string;
    gtmId: string;
    techsightUrl: string;
}

export enum LoginState {
    Failed = 'failed',
    LoggedOut = 'logged-out',
    NotStarted = 'not-started',
    Successful = 'successful',
}

export enum LoadingState {
    NotLoaded = 'not-loaded',
    Loading = 'loading',
    Successful = 'successful',
    Failed = 'failed',
}

export enum Language {
    English = 'en',
    Spanish = 'es',
    German = 'de',
    French = 'fr',
    Chinese = 'zh',
    'Chinese (Simplified)' = 'zh',
}

export type UserLanguage = keyof typeof Language;

type EquipmentNumberFilter = string[] | null;
export type EquipmentShipToFilter = { [key: string]: EquipmentNumberFilter } | null;
export interface EquipmentFilter {
    [key: string]: EquipmentShipToFilter;
}

export type CustomerUserRole = 'Operator' | 'Manager' | 'Parts purchaser' | 'Dealer';

export type InternalUserRole = 'Admin';

export type UserRole = CustomerUserRole | InternalUserRole;

export type UserFlags = {
    cookieBannerShown: boolean;
    serviceMenuHelperShown: boolean;
};

export interface SelectedCustomer {
    customerNumber: string;
    name: string;
}
export interface UserSettings {
    selectedCustomers: SelectedCustomer[];
    // TODO: move flags here too, would need PROD/PILOT/DEV DB cleanup
};
interface SessionBaseUser {
    id: string;
    email?: string;
    name?: string;
    myParts: {
        active: boolean;
        canBuy: boolean;
    };
    flags: UserFlags;
    settings: UserSettings;
}

export interface AccessRights {
    read: boolean;
    create: boolean;
}

export interface UserAccess {
    equipment: AccessRights;
    cases: AccessRights;
    maintenancePlans: AccessRights;
    maintenanceReporting: AccessRights;
    salesOrders: AccessRights;
    contacts: AccessRights;
    automationSupport: AccessRights;
    kalmarInsight: AccessRights;
    delivery: AccessRights;
    order: AccessRights;
    shippingDetails: AccessRights;
    dealerCommunity: AccessRights;
    mascusWidget: AccessRights;
    documentationPage: AccessRights;
    documentationPagePro: AccessRights; // added for pro users like dealers
    techsight: AccessRights;
    serviceRequest: AccessRights;
    trainingRequest: AccessRights;
    stockEquipment: AccessRights;
}

export interface CustomerUser extends SessionBaseUser {
    contactId: string | null;
    equipmentFilter: EquipmentFilter;
    isAdmin: false;
    role: CustomerUserRole;
    access: UserAccess;
    sapCustomerNumber: string | null;
    sapCustomerName: string | null;
    language: UserLanguage;
    accountName: string;
    dealer: boolean;
    address: {
        country: string;
        city: string | null;
        region: string | null;
    }
}

export interface InternalUser extends SessionBaseUser {
    contactId: null;
    equipmentFilter: null;
    isAdmin: true;
    role: InternalUserRole;
    access: UserAccess;
}

export type SVGComponent = React.FC<React.SVGAttributes<SVGElement>>;

export type WidgetId =
    | 'automation-support'
    | 'contacts'
    | 'documents'
    | 'event-feed'
    | 'maintenance-reporting'
    | 'maintenance-schedule'
    | 'my-cases'
    | 'my-delivery'
    | 'my-equipment'
    | 'my-parts'
    | 'kalmar-insight'
    | 'mascus'
    | 'dealer-community'
    | 'documentation-page-pro'
    | 'service-requests'
    | 'training-requests'
    | 'empty-my-cases'
    | 'empty-my-parts'
    | 'empty-insight'
    | 'techsight'
    | 'data-request'
    | 'stock-equipment'
    | 'bulletins-tab'
    | 'my-equipment-page';

interface WidgetBreadcrumbSubPage {
    icon: React.ReactNode;
    title: string;
}

export interface WidgetBreadcrumb {
    id: WidgetId;
    title: string;
    subPage?: WidgetBreadcrumbSubPage;
}

export interface Breadcrumb {
    title: string;
    pathname: string;
    key: string;
}

// Dates should be in DATE_KEY_FORMAT
export interface DateRange {
    startDate: string;
    endDate: string;
}

export interface KPIParams extends DateRange {
    splitBy?: DateTimeUnit;
    keyFormat?: TimeKeyFormat;
}

export interface InfiniteDateRange {
    startDate: string;
    endDate?: string;
}

export interface ISOTimeRange {
    startTime: string;
    endTime: string;
}

export interface InfiniteISOTimeRange {
    startTime: string;
    endTime?: string;
}

export interface InsightLicenseCounts {
    valid: number;
    expired: number;
    expiring: number;
}

export enum EquipmentType {
    COUNTER_BALANCED = 'Counter Balanced',
    TERMINAL_TRACTOR = 'Terminal Tractor',
    EMPTY_CONTAINER_HANDLER = 'Empty Container Handler',
    FORKLIFT_TRUCK = 'Forklift Truck',
    REACHSTACKER = 'Reachstacker',
    OTHER = 'Other',
    ROUGH_TERRAIN_CONTAINER_HANDLER = 'Rough Terrain Container Handler',
    // TODO: Remove once this list is stable
    // AUTOMATED_GUIDED_VEHICLE = 'Automated Guided Vehicle',
    // AUTOMATIC_RAIL_MOUNTED_GANTRY_CRANE = 'Automatic Rail-Mounted Gantry Crane',
    // AUTOMATIC_RUBBER_TYRED_GANTRY_CRANE = 'Automatic Rubber Tyred Gantry Crane',
    // AUTOMATIC_STACKING_CRANE = 'Automatic Stacking Crane',
    // AUTOMATED_SHUTTLE_CARRIER = 'Automated Shuttle Carrier',
    // COUNTER_BALANCE_GENERAL = 'Counter Balance (General)',
    // TERMINAL_TRACTOR = 'Terminal Tractor',
    // OTHER_EQUIPMENT = 'Other Equipment',
    // EMPTY_CONTAINER_HANDLER = 'Empty Container Handler',
    // FORKLIFT_TRUCK = 'Forklift Truck',
    // REACHSTACKER = 'Reachstacker',
    // LOGSTACKER = 'Logstacker',
    // RAIL_MOUNTED_GANTRY_CRANE = 'Rail-Mounted Gantry Crane',
    // ROUGH_TERRAIN_CONTAINER_HANDLER = 'Rough Terrain Container Handler',
    // RUBBER_TYRED_GANTRY_CRANE = 'Rubber Tyred Gantry Crane',
    // STRADDLE_CARRIER = 'Straddle Carrier',
    // SHUTTLE_CARRIER = 'Shuttle Carrier',
    // SPREADER = 'Spreader',
    // SHIP_TO_SHORE_CRANE = 'Ship-to-Shore Crane',
    // LIFTING_DEVICE_BARTLETT = 'Lifting Device (Bartlett)',
}
